// Styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';

// NPM Modules
import ImageZoom from 'js-image-zoom';
import Swiper from 'swiper';
import {
  Navigation,
  Controller,
  Manipulation,
  Pagination,
  Thumbs,
  FreeMode,
  Scrollbar,
  Autoplay,
} from 'swiper/modules';

// Swiper 14 injects its own arrow SVG into empty .swiper-button-next/prev
// elements (navigation.addIcons defaults to true), duplicating the theme's
// own chevrons. Keep the v11 behavior: theme renders its own icons.
Swiper.extendDefaults({ navigation: { addIcons: false } });

// Cusom Functions
import { screens } from '../modules-custom/functions';
import { ensureLazyGallerySrc } from '../modules-custom/lazyLoadGalleryLot';

import { W8ShippingAPIClient } from '../../../assets/js/modules-custom/W8_ShippingAPIClient';

const client = new W8ShippingAPIClient(
  theme.api.endpointW8,
  theme.api.nonce,
  theme.i18n.locale_pll,
);
document.addEventListener('DOMContentLoaded', function () {
  // Set Sliders
  setSwiperLots();

  // Slider with photos on lot card
  setSwiperLot();

  // Gallery and Zoom on hover
  setSwiperGalleryWithZoom();

  // Comparelist Slider
  seSwipersCompare();

  // Reviews Slider
  setSwiperReviews();

  // Video Slider
  setSwiperVideo();

  // Clients Autos Slider
  setSwiperClientsAutos();

  // Profitable Autos Slider
  setSwiperProfitableAutos();

  setDeliveryControl();

  // Autos Compare Slider
  setSwiperAutosCompare();

  // Footer Autos Slider
  setSwiperFooterAutos();
  // Tools Slider
  setToolsSwiper();

  // Tools v2 Slider
  setToolsV2Swiper();

  // Posts v2 Slider
  setSwiperPosts();

  setSwiperBrands();
  setCargoTracking();
  setCalcSwiper();
  setCalcSwiperInput();

  new Swiper('.swiper-post', {
    modules: [FreeMode, Scrollbar],
    slidesPerView: 'auto',
    // spaceBetween: 24,
    grabCursor: true,
    freeMode: true,
    // And if we need scrollbar
    scrollbar: {
      el: '.swiper-scrollbar',
      draggable: true,
    },
  });

  setLandingOfferFirstAutos();
  setLandingAutosFirstAutos();
  setLandingReviewsFirstReviews();

  // Dealer Cabinet — slider with light/dark theme toggle
  setDealerCabinetSliders();

  // Warehouses block — popup modal with locations map slider
  setWarehousesPopups();
});

/**
 * Warehouses block popup — opens a fullscreen modal with a slider of per-location YouTube videos.
 * - Any element with [data-warehouses-open-popup] opens the popup at slide
 *   given by its data-slide-index (defaults to 0). Used by the main "Watch video"
 *   button AND by each location card that has a video.
 * - Video swiper drives a synced title swiper (both show same active slide)
 * - YouTube iframes are lazy-loaded (only injected for active slide) to keep page light
 * - All iframes are removed on slide change / popup close to stop playback (no audio bleed)
 * - Close on: close button, backdrop click, Escape key
 */
function setWarehousesPopups() {
  const blocks = document.querySelectorAll('[id^="warehouses-"]');
  if (!blocks.length) return;

  blocks.forEach(function (root) {
    if (root.dataset.warehousesInited === '1') return;
    root.dataset.warehousesInited = '1';

    const openTriggers = root.querySelectorAll('[data-warehouses-open-popup]');
    const popup = root.querySelector('[data-warehouses-popup]');
    if (!openTriggers.length || !popup) return;

    const closeBtn = popup.querySelector('[data-warehouses-popup-close]');
    const videoEl = popup.querySelector('[data-popup-video]');
    const titleEl = popup.querySelector('[data-popup-title]');
    const prevBtn = popup.querySelector('[data-popup-prev]');
    const nextBtn = popup.querySelector('[data-popup-next]');
    const counterCurrent = popup.querySelector('[data-popup-current]');

    let videoSwiper = null;

    function updateTitle(activeIndex) {
      if (!titleEl) return;
      const slot = popup.querySelectorAll('[data-video-slide]')[activeIndex];
      if (slot && slot.dataset.videoTitle) {
        titleEl.textContent = slot.dataset.videoTitle;
      }
    }

    // Insert YouTube iframe for a given slide index — destroying any others to stop playback
    function loadVideoForSlide(activeIndex) {
      popup.querySelectorAll('[data-video-slide]').forEach(function (slot, i) {
        if (i === activeIndex) {
          // Inject iframe if not present
          if (!slot.querySelector('iframe')) {
            const videoId = slot.dataset.videoId;
            if (videoId) {
              const iframe = document.createElement('iframe');
              // Iframe is injected on user click (button / card) → counts as a
              // user gesture, so browsers usually allow autoplay WITH sound here.
              // If a browser still blocks it, YouTube falls back to a play button.
              // playsinline=1 prevents iOS Safari from going fullscreen on play.
              iframe.src =
                'https://www.youtube.com/embed/' +
                encodeURIComponent(videoId) +
                '?autoplay=1&rel=0&playsinline=1';
              iframe.title = 'YouTube video';
              iframe.className = 'w-full h-full block';
              iframe.allow =
                'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
              iframe.referrerPolicy = 'strict-origin-when-cross-origin';
              iframe.allowFullscreen = true;
              slot.appendChild(iframe);
            }
          }
        } else {
          // Remove iframes from inactive slides → stops playback
          const iframe = slot.querySelector('iframe');
          if (iframe) iframe.remove();
        }
      });
    }

    function open(slideIndex) {
      const targetIndex =
        typeof slideIndex === 'number' && slideIndex >= 0 ? slideIndex : 0;

      popup.classList.remove('hidden');
      popup.classList.add('flex');
      popup.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Defer Swiper init / update until AFTER the popup is actually laid out.
      // Without this, Swiper measures its container width as 0 (popup was display:none
      // a moment ago) and slides end up zero-sized → black box, no visible video.
      requestAnimationFrame(function () {
        if (!videoSwiper && videoEl) {
          videoSwiper = new Swiper(videoEl, {
            modules: [Navigation],
            spaceBetween: 0,
            slidesPerView: 1,
            initialSlide: targetIndex,
            navigation: { prevEl: prevBtn, nextEl: nextBtn },
            on: {
              slideChange: function () {
                if (counterCurrent)
                  counterCurrent.textContent = this.activeIndex + 1;
                updateTitle(this.activeIndex);
                loadVideoForSlide(this.activeIndex);
              },
            },
          });
          // initialSlide doesn't fire slideChange, so manually sync title/counter/iframe
          if (counterCurrent) counterCurrent.textContent = targetIndex + 1;
          updateTitle(targetIndex);
          loadVideoForSlide(targetIndex);
        } else if (videoSwiper) {
          // Subsequent opens: re-measure (popup was hidden), then jump
          videoSwiper.update();
          if (videoSwiper.activeIndex !== targetIndex) {
            videoSwiper.slideTo(targetIndex, 0);
          } else {
            // Same slide — re-inject iframe (it was removed on previous close)
            loadVideoForSlide(targetIndex);
            updateTitle(targetIndex);
          }
        }
      });
    }

    function close() {
      popup.classList.add('hidden');
      popup.classList.remove('flex');
      popup.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      // Stop playback by removing all iframes
      popup
        .querySelectorAll('[data-video-slide] iframe')
        .forEach(function (iframe) {
          iframe.remove();
        });
    }

    // Wire every open-trigger (main button + each clickable card)
    openTriggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        const raw = trigger.dataset.slideIndex;
        const idx = raw !== undefined ? parseInt(raw, 10) : 0;
        open(isNaN(idx) ? 0 : idx);
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', close);

    // Close on backdrop click (only when clicking the outer overlay, not inner content)
    popup.addEventListener('click', function (e) {
      if (e.target === popup) close();
    });

    // Close on Escape key (only when popup is open)
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !popup.classList.contains('hidden')) close();
    });
  });
}

/**
 * Dealer Cabinet block slider.
 * - Two synced Swipers (images + texts); the text follows the images via
 *   slideTo(realIndex) on slideChange (loop-safe, unlike the Controller module).
 * - Pagination dots + prev/next arrows.
 * - Light/Dark theme toggle that swaps img variant (independent of slide nav).
 */
function setDealerCabinetSliders() {
  const blocks = document.querySelectorAll('[id^="dealer-cabinet-"]');
  if (!blocks.length) return;

  blocks.forEach(function (root) {
    if (root.dataset.cabinetInited === '1') return;
    root.dataset.cabinetInited = '1';

    const imagesEl = root.querySelector('[data-cabinet-images]');
    const textEl = root.querySelector('[data-cabinet-text]');
    const prevBtn = root.querySelector('[data-cabinet-prev]');
    const nextBtn = root.querySelector('[data-cabinet-next]');
    const paginationEl = root.querySelector('[data-cabinet-pagination]');
    const counterCurrent = root.querySelector('[data-cabinet-current]');
    const wrapper = root.querySelector('[data-dealer-cabinet]');

    if (!imagesEl || !textEl || !wrapper) return;

    // Text swiper (read-only — driven by the images swiper).
    // No autoHeight: slides stretch to the tallest, so the text area keeps a
    // CONSTANT height across slides — only the text content changes, the card /
    // pagination / arrows never reflow (no page "shake" on autoplay).
    const textSwiper = new Swiper(textEl, {
      spaceBetween: 0,
      slidesPerView: 1,
      allowTouchMove: false,
    });

    // Main images swiper drives the text swiper (synced in slideChange below).
    // Auto-rotates every 3 seconds per spec (point 9 of dealers fixes PDF).
    // eslint-disable-next-line no-unused-vars
    const imagesSwiper = new Swiper(imagesEl, {
      modules: [Navigation, Pagination, Autoplay],
      spaceBetween: 0,
      slidesPerView: 1,
      loop: true,
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      },
      navigation: { prevEl: prevBtn, nextEl: nextBtn },
      pagination: { el: paginationEl, clickable: true },
      on: {
        slideChange: function () {
          if (counterCurrent) counterCurrent.textContent = this.realIndex + 1;
          // Sync the text swiper by REAL index. Swiper's Controller desyncs here
          // because the images swiper loops and the text swiper doesn't — loop
          // duplicates shift activeIndex, leaving the text stuck. realIndex (0..n-1)
          // always maps to the real text slide, so this stays in sync past the loop.
          textSwiper.slideTo(this.realIndex);
        },
      },
    });

    // Press feedback on the prev/next buttons — made bullet-proof with THREE redundant
    // paths so at least one always fires the red fill on every tap, on every platform:
    //   1. CSS :active (see dealer-cabinet.twig) — native press state; the empty touchstart
    //      listener below is what makes iOS Safari actually apply :active to a <button>.
    //   2. Direct pointerdown/touchstart/click on the buttons → .is-pressing flash (held
    //      350ms so it's clearly visible even on a quick tap).
    //   3. Swiper's own slideNext/PrevTransitionStart, gated to a recent MANUAL tap (caught
    //      by capture-phase listeners on the root, which run before Swiper can consume the
    //      event) — covers the case where the button's own events get swallowed after the
    //      first navigation. Autoplay (no preceding manual tap) never flashes.
    const flash = function (btn) {
      if (!btn) return;
      btn.classList.add('is-pressing');
      clearTimeout(btn._pressTimer);
      btn._pressTimer = setTimeout(function () {
        btn.classList.remove('is-pressing');
      }, 350);
    };
    let lastManual = 0;
    const markManual = function (e) {
      const t = e.target;
      if ((prevBtn && prevBtn.contains(t)) || (nextBtn && nextBtn.contains(t))) {
        lastManual = Date.now();
      }
    };
    ['pointerdown', 'touchstart', 'click'].forEach(function (ev) {
      root.addEventListener(ev, markManual, true);
    });
    const manualRecently = function () {
      return Date.now() - lastManual < 1200;
    };
    [prevBtn, nextBtn].forEach(function (btn) {
      if (!btn) return;
      const onPress = function () {
        flash(btn);
      };
      // passive empty/flash touchstart also unlocks the :active CSS state on iOS Safari
      btn.addEventListener('touchstart', onPress, { passive: true });
      btn.addEventListener('pointerdown', onPress);
      btn.addEventListener('click', onPress);
    });
    imagesSwiper.on('slideNextTransitionStart', function () {
      if (manualRecently()) flash(nextBtn);
    });
    imagesSwiper.on('slidePrevTransitionStart', function () {
      if (manualRecently()) flash(prevBtn);
    });

    // Theme toggle (light/dark) — independent from slide navigation
    root.querySelectorAll('[data-theme-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const theme = btn.getAttribute('data-theme-toggle');
        wrapper.setAttribute('data-theme', theme);
      });
    });
  });
}

// Comparelist Slider
document.addEventListener('compare-add', function () {
  seSwipersCompare();
});
document.addEventListener('compare-remove', function () {
  seSwipersCompare();
});

function setCargoTracking() {
  const el = document.getElementById('swiper-cargo-tracking');
  if (!el) {
    return;
  }

  const swiper = new Swiper(el, {
    autoHeight: true,
    oneWayMovement: true,
    allowTouchMove: false,
  });

  document
    .getElementById('swiper-cargo-tracking-back')
    ?.addEventListener('click', () => {
      const currentUrl = window.location.href;
      const urlWithoutQueryParams = currentUrl.split('?')[0];
      history.replaceState(null, '', urlWithoutQueryParams);

      swiper.slidePrev(800);
    });
}

function setCalcSwiperInput() {
  const el = document.getElementById('swiper-calc-input');
  if (!el) {
    return;
  }

  const swiper = new Swiper(el, {
    autoHeight: true,
    oneWayMovement: true,
    allowTouchMove: false,
    spaceBetween: 24,
  });

  document
    .querySelector('[data-calc]')
    ?.addEventListener('submit', async (e) => {
      e.preventDefault();
      swiper.slideNext(800);
    });

  document.addEventListener('calculated', () => {
    swiper.update();
  });
  document.addEventListener('changeVehicleType', () => {
    swiper.update();
  });
}

function setToolsV2Swiper() {
  document.querySelectorAll('.tools-v2-swiper')?.forEach((el) => {
    const swiperID = el.dataset.id;
    const buttonPrev = document.getElementById(
      'swiper-button-prev-' + swiperID,
    );
    const buttonNext = document.getElementById(
      'swiper-button-next-' + swiperID,
    );

    // Если стрелки не найдены — пропускаем
    if (!buttonPrev || !buttonNext) return;

    new Swiper(el, {
      modules: [Navigation],
      grabCursor: true,
      watchSlidesProgress: true,
      spaceBetween: 16,
      speed: 800,
      navigation: {
        prevEl: buttonPrev,
        nextEl: buttonNext,
        hiddenClass: 'opacity-50',
      },
      slidesPerView: 1.1,
      breakpoints: {
        640: {
          slidesPerView: 1.5,
        },
        768: {
          slidesPerView: 2,
        },
        1024: {
          slidesPerView: 2,
        },
        1280: {
          slidesPerView: 3,
        },
        1536: {
          slidesPerView: 3,
        },
      },
    });
  });
}

function setSwiperPosts() {
  const isMobile = window.matchMedia('(max-width: 767px)').matches;

  document.querySelectorAll('.swiper-posts')?.forEach((el) => {
    if (!isMobile) return;

    const swiperID = el.dataset.id;
    const buttonPrev = document.getElementById(
      'swiper-button-prev-' + swiperID,
    );
    const buttonNext = document.getElementById(
      'swiper-button-next-' + swiperID,
    );
    const paginationCurrent = document.getElementById(
      'swiper-pagination-current-' + swiperID,
    );

    if (!buttonPrev || !buttonNext) return;

    new Swiper(el, {
      modules: [Navigation],
      grabCursor: true,
      watchSlidesProgress: true,
      spaceBetween: 16,
      speed: 500,
      slidesPerView: 1.05,
      centeredSlides: false,
      navigation: {
        prevEl: buttonPrev,
        nextEl: buttonNext,
        hiddenClass: 'opacity-50',
      },
      on: {
        slideChange: function () {
          if (paginationCurrent) {
            paginationCurrent.textContent = this.activeIndex + 1;
          }
        },
      },
    });
  });
}

function setToolsSwiper() {
  document
    .querySelectorAll('.tools-swiper:not(.tools-v2-swiper)')
    ?.forEach((el) => {
      const swiperID = el.dataset.id;
      const buttonPrev = document.getElementById(
        'swiper-button-prev-' + swiperID,
      );
      const buttonNext = document.getElementById(
        'swiper-button-next-' + swiperID,
      );

      // Если стрелки не найдены — пропускаем
      if (!buttonPrev || !buttonNext) return;

      new Swiper(el, {
        modules: [Navigation, Autoplay],
        grabCursor: true,
        watchSlidesProgress: true,
        spaceBetween: 24,
        speed: 800,
        navigation: {
          prevEl: buttonPrev,
          nextEl: buttonNext,
          hiddenClass: 'opacity-50',
        },
        slidesPerView: 1,
        breakpoints: {
          640: {
            slidesPerView: 1.6,
          },
          768: {
            slidesPerView: 1.8,
          },
          1024: {
            slidesPerView: 2.2,
          },
          1280: {
            slidesPerView: 1.5,
          },
          1536: {
            slidesPerView: 2.5,
          },
        },
      });

      // обработка активного слайда по клику
      el.querySelectorAll('.swiper-slide').forEach((slide) => {
        slide.addEventListener('click', () => {
          el.querySelectorAll('.swiper-slide').forEach((s) =>
            s.classList.remove('active'),
          );
          slide.classList.add('active');
        });
      });
    });
}

function setCalcSwiper() {
  const el = document.getElementById('swiper-calc');
  if (!el) {
    return;
  }

  const swiper = new Swiper(el, {
    autoHeight: true,
    oneWayMovement: true,
    allowTouchMove: false,
  });

  document.getElementById('swiper-calc-back')?.addEventListener('click', () => {
    swiper.slidePrev(800);
  });

  document.getElementById('payment-steps')?.addEventListener('click', () => {
    swiper.slideNext(800);
  });

  document.addEventListener('calculated', () => {
    swiper.update();
  });
  document.addEventListener('changeVehicleType', () => {
    swiper.update();
  });
}

function setSwiperLots() {
  document.querySelectorAll('.swiper-lots')?.forEach((el) => {
    const lotID = el.dataset.id;
    const buttonPrev = document.getElementById('swiper-button-prev-' + lotID);
    const buttonNext = document.getElementById('swiper-button-next-' + lotID);
    const pagination = document.getElementById('swiper-pagination-' + lotID);
    const isOffer = el.classList.contains('swiper-lots-offer');
    const isAutosTo = el.classList.contains('swiper-autos-to');

    if (isAutosTo && window.innerWidth >= screens.lg) {
      return;
    }

    new Swiper(el, {
      modules: [Navigation, Manipulation, Pagination],
      grabCursor: true,
      watchSlidesProgress: true,
      spaceBetween: 12,
      speed: 1000,
      pagination: {
        el: pagination,
        type: 'fraction',
      },
      breakpoints: {
        640: {
          slidesPerView: 2.2,
        },
        768: {
          slidesPerView: 2.5,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 16,
          // slidesPerGroup: 3,
          // speed: 1600
        },
        1280: {
          slidesPerView: isOffer ? 1.5 : 4,
          spaceBetween: 16,

          // slidesPerGroup: 4,
          // speed: 1600
        },
        1536: {
          slidesPerView: isOffer ? 2 : 4,
          spaceBetween: 16,
          // slidesPerGroup: 4,
          // speed: 1600
        },
      },
      navigation: {
        prevEl: buttonPrev,
        nextEl: buttonNext,
        hiddenClass: 'opacity-50',
      },
    });
  });
}

function setSwiperAutosCompare() {
  document.querySelectorAll('.swiper-autos-compare')?.forEach((el) => {
    const lotID = el.dataset.id;
    const buttonPrev = document.getElementById('swiper-button-prev-' + lotID);
    const buttonNext = document.getElementById('swiper-button-next-' + lotID);
    const pagination = document.getElementById('swiper-pagination-' + lotID);
    const type = el.dataset.type;
    const typeList = el.dataset.typeList;
    const query = el.dataset.query;

    const swiper = new Swiper(el, {
      modules: [Navigation, Manipulation, Pagination],
      grabCursor: true,
      watchSlidesProgress: true,
      spaceBetween: 12,
      speed: 1000,
      pagination: {
        el: pagination,
        type: 'fraction',
      },
      breakpoints: {
        1024: {
          slidesPerView: theme.i18n.locale == 'kk-KZ' ? 2 : 1.5,
          spaceBetween: 16,
          // slidesPerGroup: 3,
          // speed: 1600
        },
        1280: {
          slidesPerView: theme.i18n.locale == 'kk-KZ' ? 2.5 : 'auto',
          spaceBetween: 16,
          // slidesPerGroup: 4,
          // speed: 1600
        },
      },
      navigation: {
        prevEl: buttonPrev,
        nextEl: buttonNext,
        hiddenClass: 'opacity-50',
      },
      on: {
        slideChange: function () {
          if (this.isLoadedLazySlides) {
            return;
          }
          updateSwiper(swiper, type, query, typeList);
          this.isLoadedLazySlides = true;
        },
      },
    });
  });
}

function updateSwiper(swiper, type, query, typeList = null, extra = {}) {
  const event_name = 'swiper-' + type + '-updated';

  const event = new CustomEvent(event_name);

  client.post('getSwiperItems', {
    body: {
      type: type,
      typeList: typeList,
      query: query,
      ...extra,
    },
    onSuccess: ({ items }) => {
      if (items.length) {
        swiper.slidesEl
          .querySelectorAll('.swiper-slide.skeleton')
          .forEach((slide, index) => {
            const container = document.createElement('div');
            container.innerHTML = items[index];
            const newSlide = container.querySelector(`.swiper-slide`);
            swiper.slidesEl.replaceChild(newSlide, slide);
          });
        swiper.update();
        document.dispatchEvent(event);
      }
      swiper.enable();
    },
  });
}

function setSwiperReviews() {
  document.querySelectorAll('.swiper-reviews')?.forEach((el) => {
    const ID = el.dataset.id;
    const buttonPrev = document.getElementById('swiper-button-prev-' + ID);
    const buttonNext = document.getElementById('swiper-button-next-' + ID);
    const pagination = document.getElementById('swiper-pagination-' + ID);
    const type = el.dataset.type;
    const typeList = el.dataset.typeList;
    const query = el.dataset.query;

    const swiper = new Swiper(el, {
      modules: [Navigation, Manipulation, Pagination],
      grabCursor: true,
      pagination: {
        el: pagination,
        type: 'fraction',
      },
      watchSlidesProgress: true,
      spaceBetween: 8,
      speed: 1000,
      breakpoints: {
        640: {
          slidesPerView: 2,
          spaceBetween: 16,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 16,
        },
      },
      navigation: {
        prevEl: buttonPrev,
        nextEl: buttonNext,
        hiddenClass: 'opacity-50',
      },
      on: {
        slideChange: function () {
          const showMore =
            this.slides[this.previousIndex].querySelector(
              '.ShowMoreLC',
            )?.showMore;
          if (showMore && window.innerWidth <= 640) {
            showMore.hideContent();
          }
          if (this.isLoadedLazySlides) {
            return;
          }
          updateSwiper(swiper, type, query, typeList);
          this.isLoadedLazySlides = true;
        },
      },
    });
  });
}

function setSwiperBrands() {
  document.querySelectorAll('.swiper-brands')?.forEach((el) => {
    const ID = el.dataset.id;
    const buttonPrev = document.getElementById('swiper-button-prev-' + ID);
    const buttonNext = document.getElementById('swiper-button-next-' + ID);

    new Swiper(el, {
      modules: [Navigation, Manipulation],
      grabCursor: true,
      spaceBetween: 12,
      slidesPerView: 'auto',
      speed: 1000,
      breakpoints: {
        640: {
          spaceBetween: 24,
        },
      },
      navigation: {
        prevEl: buttonPrev,
        nextEl: buttonNext,
        hiddenClass: 'opacity-50',
      },
    });
  });
}

function setSwiperVideo() {
  document.querySelectorAll('.swiper-video')?.forEach((el) => {
    const ID = el.dataset.id;
    const buttonPrev = document.getElementById('swiper-button-prev-' + ID);
    const buttonNext = document.getElementById('swiper-button-next-' + ID);
    const pagination = document.getElementById('swiper-pagination-' + ID);

    new Swiper(el, {
      modules: [Navigation, Manipulation, Pagination],
      grabCursor: true,
      pagination: {
        el: pagination,
        type: 'fraction',
      },
      watchSlidesProgress: true,
      spaceBetween: 8,
      speed: 1000,
      breakpoints: {
        640: {
          slidesPerView: 2,
          spaceBetween: 16,
        },
      },
      navigation: {
        prevEl: buttonPrev,
        nextEl: buttonNext,
        hiddenClass: 'opacity-50',
      },
    });
  });
}

function setSwiperClientsAutos() {
  document.querySelectorAll('.swiper-clients-autos')?.forEach((el) => {
    const ID = el.dataset.id;
    const buttonPrev = document.getElementById('swiper-button-prev-' + ID);
    const buttonNext = document.getElementById('swiper-button-next-' + ID);
    const pagination = document.getElementById('swiper-pagination-' + ID);
    const type = el.dataset.type;
    const typeList = el.dataset.typeList;
    const query = el.dataset.query;

    const swiper = new Swiper(el, {
      modules: [Navigation, Manipulation, Pagination],
      grabCursor: true,
      pagination: {
        el: pagination,
        type: 'fraction',
      },
      watchSlidesProgress: true,
      spaceBetween: 8,
      // autoHeight: true,
      speed: 500,
      breakpoints: {
        640: {
          slidesPerView: 2,
          spaceBetween: 16,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 16,
        },
      },
      navigation: {
        prevEl: buttonPrev,
        nextEl: buttonNext,
        hiddenClass: 'opacity-50',
      },
      on: {
        slideChange: function () {
          if (this.isLoadedLazySlides) {
            return;
          }
          updateSwiper(swiper, type, query, typeList);
          this.isLoadedLazySlides = true;
        },
      },
    });
  });
}

function setSwiperProfitableAutos() {
  document.querySelectorAll('.swiper-autos-profitable')?.forEach((el) => {
    const ID = el.dataset.id;
    const buttonPrev = document.getElementById('swiper-button-prev-' + ID);
    const buttonNext = document.getElementById('swiper-button-next-' + ID);
    const pagination = document.getElementById('swiper-pagination-' + ID);
    const type = el.dataset.type;
    const typeList = el.dataset.typeList;
    const query = el.dataset.query;
    const currency = el.dataset.currency || 'USD';
    const market = el.dataset.market || 'ukrainian';

    const swiper = new Swiper(el, {
      modules: [Navigation, Manipulation, Pagination],
      grabCursor: true,
      pagination: {
        el: pagination,
        type: 'bullets',
      },
      watchSlidesProgress: true,
      spaceBetween: 8,
      // autoHeight: true,
      speed: 500,
      breakpoints: {
        640: {
          slidesPerView: 2,
          spaceBetween: 16,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 16,
        },
      },
      navigation: {
        prevEl: buttonPrev,
        nextEl: buttonNext,
        hiddenClass: 'opacity-50',
      },
      on: {
        slideChange: function () {
          if (this.isLoadedLazySlides) {
            return;
          }
          updateSwiper(swiper, type, query, typeList, { currency, market });
          this.isLoadedLazySlides = true;
        },
      },
    });
  });
}

function setSwiperFooterAutos() {
  document.querySelectorAll('.swiper-footer-autos')?.forEach((el) => {
    const ID = el.dataset.id;
    const buttonPrev = document.getElementById('swiper-button-prev-' + ID);
    const buttonNext = document.getElementById('swiper-button-next-' + ID);
    const pagination = document.getElementById('swiper-pagination-' + ID);

    new Swiper(el, {
      modules: [Navigation, Manipulation, Pagination],
      grabCursor: true,
      pagination: {
        el: pagination,
        type: 'fraction',
      },
      watchSlidesProgress: true,
      slidesPerView: 1.2,
      spaceBetween: 18,
      // autoHeight: true,
      speed: 500,
      breakpoints: {
        640: {
          slidesPerView: 2,
        },
        1024: {
          slidesPerView: 2.5,
        },
        1280: {
          slidesPerView: 3.2,
        },
        1536: {
          slidesPerView: 4,
        },
      },
      navigation: {
        prevEl: buttonPrev,
        nextEl: buttonNext,
        hiddenClass: 'opacity-50',
      },
    });
  });
}

function setDeliveryControl() {
  const scrollContainer = document.getElementById('scroll-container');
  const dots = document.querySelectorAll('.progress-dot');
  const car = document.querySelector('.car');
  const ProgressBar = document.getElementById('ProgressBar');

  if (!scrollContainer || !dots.length || !car || !ProgressBar) {
    return;
  }

  const swiper = new Swiper(scrollContainer, {
    spaceBetween: 10,
    resistanceRatio: 0,
    breakpoints: {
      640: {
        slidesPerView: 1.5,
      },
      768: {
        slidesPerView: 2,
        spaceBetween: 16,
      },
      1024: {
        slidesPerView: 3,
      },
      1280: {
        slidesPerView: 4,
      },
      1536: {
        slidesPerView: 4.5,
      },
    },
    on: {
      init() {
        updateActiveDots(this.activeIndex);
      },
      slideChange() {
        triggerCarAnimation();
        if (!this.isEnd) {
          updateActiveDots(this.activeIndex);
          ProgressBar.classList.remove('line-scroll-end');
        }
      },
      reachEnd() {
        updateActiveDots(this.slides.length - 1);
        ProgressBar.classList.add('line-scroll-end');
      },
    },
  });

  function triggerCarAnimation() {
    car.classList.remove('car-rev-active');
    void car.offsetWidth;
    car.classList.add('car-rev-active');
  }

  function updateActiveDots(activeIdx) {
    dots.forEach((dot, i) => dot.classList.toggle('active', i <= activeIdx));
  }

  let currentSlide = 0;
  const totalSlides = swiper.slides.length;

  // Динамічний імпорт GSAP
  import('gsap').then(({ gsap }) => {
    import('gsap/ScrollTrigger').then(({ ScrollTrigger }) => {
      gsap.registerPlugin(ScrollTrigger);

      const snap = gsap.utils.snap(1 / totalSlides);

      ScrollTrigger.create({
        trigger: '.delivery-control',
        start: 'bottom bottom',
        end: 'bottom center',
        pin: false,
        // markers: true,
        onUpdate: (self) => {
          const updatedIndex = Math.round(snap(self.progress) * totalSlides);
          if (updatedIndex !== currentSlide) {
            currentSlide = updatedIndex;
            swiper.slideTo(currentSlide);
          }
        },
      });
    });
  });

  // Клік на dots
  dots.forEach((dot) => {
    dot.addEventListener('click', () => {
      const idx = parseInt(dot.dataset.idx, 10);
      if (isNaN(idx)) return;
      const currentIdx = swiper.activeIndex;
      const dist = Math.abs(idx - currentIdx);
      if (dist > 0) {
        const animSpeed = dist * 800;
        swiper.slideTo(idx, animSpeed);
      }
    });
  });
}

function loadLotSlide(swiper, index) {
  const slide = swiper.slides[index];
  if (!slide) return;
  slide.querySelectorAll('img[data-src]').forEach((img) => {
    if (img.getAttribute('src')) return;
    img.setAttribute('src', img.dataset.src);
  });
}

function preloadLotSlides(swiper) {
  loadLotSlide(swiper, swiper.activeIndex);
  loadLotSlide(swiper, swiper.activeIndex + 1);
}

function setSwiperLot() {
  document.querySelectorAll('.swiper-lot')?.forEach((el) => {
    if (el.swiper) return;
    const lotID = el.dataset.lotId;
    const pagination = document.getElementById('swiper-pagination-' + lotID);
    const buttonPrev = document.getElementById('swiper-button-prev-' + lotID);
    const buttonNext = document.getElementById('swiper-button-next-' + lotID);

    const swiper = new Swiper(el, {
      modules: [Pagination, Navigation],
      initialSlide: 0,
      slidesPerView: 1,
      navigation: {
        prevEl: buttonPrev,
        nextEl: buttonNext,
        hiddenClass: 'opacity-50',
      },
      pagination: {
        el: pagination,
        clickable: true,
        dynamicBullets: true,
      },
      on: {
        init: preloadLotSlides,
        slideChangeTransitionStart: preloadLotSlides,
      },
    });
    preloadLotSlides(swiper);
  });
}

function ensureGallerySlide(slide) {
  const img = slide?.querySelector('img[data-lazy-src]');
  if (!img) return;
  ensureLazyGallerySrc(img.dataset.lazySrc);
}

function preloadGallerySlides(swiper) {
  ensureGallerySlide(swiper.slides[swiper.activeIndex]);
  ensureGallerySlide(swiper.slides[swiper.activeIndex + 1]);
}

function preloadThumbWindow(swiper) {
  const visible = Number(swiper.params.slidesPerView) || 4;
  const start = swiper.activeIndex;
  for (let index = start; index <= start + visible; index++) {
    ensureGallerySlide(swiper.slides[index]);
  }
}

function setSwiperGalleryWithZoom() {
  const galleryEl = document.getElementById('lot-gallery');
  if (!galleryEl) return;

  let imageZoom;
  const thumbEl = document.getElementById('lot-gallery-thumb');
  const thumb = thumbEl
    ? new Swiper(thumbEl, {
        modules: [FreeMode, Thumbs],
        slidesPerView: 4,
        spaceBetween: 2,
        freeMode: true,
        watchSlidesProgress: true,
      })
    : null;

  if (thumb) {
    thumb.on('activeIndexChange', () => preloadThumbWindow(thumb));
  }

  const swiper = new Swiper(galleryEl, {
    modules: [Navigation, Thumbs, Pagination],
    spaceBetween: 12,
    navigation: {
      nextEl: '.auction-car-gallery-next',
      prevEl: '.auction-car-gallery-prev',
      hiddenClass: 'hidden',
    },
    pagination: {
      modifierClass: 'single-lot-swiper-pagination-',
      el: document.getElementById('swiper-pagination-lot-gallery'),
      type: 'fraction',
    },
    thumbs: thumb ? { swiper: thumb } : undefined,
    on: {
      init: zoom,
      slideChangeTransitionStart: preloadGallerySlides,
    },
  });

  function zoom(swiper) {
    if (window.innerWidth < screens.lg) {
      return;
    }
    imageZoom?.kill();
    imageZoom = new ImageZoom(swiper.slides[swiper.realIndex], {
      zoomContainer: document.getElementById('info-lot'),
      width: swiper.slides[swiper.realIndex].clientWidth,
      zoomPosition: 'original',
    });
  }

  swiper.on('slideChangeTransitionEnd', zoom);
}

function seSwipersCompare() {
  const updateSwiperSlides = (swiper) => {
    const currentBreakpoint = swiper.currentBreakpoint || 'max';
    const slidesPerView =
      currentBreakpoint === 'max'
        ? swiper.params.slidesPerView
        : swiper.params.breakpoints[currentBreakpoint].slidesPerView;

    let slidesCount = swiper.slidesEl.querySelectorAll(
      '.swiper-slide[data-slider-id]',
    ).length;

    if (slidesCount < slidesPerView) {
      // Minimum slides per view
      if (+currentBreakpoint >= 640 && slidesCount < 2) {
        slidesCount = 2;
      }
      swiper.params.slidesPerView = slidesCount;
    }

    swiper.update();
  };

  // sync all sliders
  let isSwiping = false; // to prevent recursive loop
  document.compareSwipers = [];

  document.querySelectorAll('.swiper-main')?.forEach((el, index) => {
    new Swiper(el, {
      // Your Swiper settings here
      // init: false,
      modules: [Navigation, Controller, Manipulation], // Ensure you include the Controller module
      slidesPerView: 1.7,
      spaceBetween: index == 0 ? 6 : 0,
      mobileFirst: true,
      // cssMode: true,
      breakpoints: {
        640: {
          slidesPerView: 2.2,
          spaceBetween: index == 0 ? 10 : 0,
        },
        768: {
          slidesPerView: 2.5,
          spaceBetween: index == 0 ? 16 : 0,
        },
        1024: {
          slidesPerView: 3.2,
          spaceBetween: index == 0 ? 16 : 0,
          noSwiping: true,
          noSwipingClass: 'swiper-slide',
        },
        1280: {
          slidesPerView: 4,
          spaceBetween: index == 0 ? 16 : 0,
          noSwiping: true,
          noSwipingClass: 'swiper-slide',
        },
      },
      navigation: {
        nextEl: index == 0 ? '.swiper-btn-next' : null,
        prevEl: index == 0 ? '.swiper-btn-prev' : null,
        hiddenClass: 'hidden',
      },
      on: {
        init: function (swiper) {
          document.compareSwipers.push(this);
          updateSwiperSlides(swiper);
        },
        slideChange: function () {
          if (!isSwiping) {
            const activeIndex = this.activeIndex;
            isSwiping = true;
            document.compareSwipers.forEach((sw) => {
              if (sw !== this) {
                sw.slideTo(activeIndex);
              }
            });
            isSwiping = false;
          }
        },
        breakpoint: function (swiper) {
          updateSwiperSlides(swiper);
        },
      },
    });
  });
}

document.addEventListener('related-lots-loaded', function () {
  setSwiperLot();
});

document.addEventListener('facetwp-loaded', function () {
  setSwiperLot();
});

function setLandingOfferFirstAutos() {
  if (!document.getElementById('offer-1-autos')) return;

  const swiper = new Swiper('#offer-1-autos', {
    speed: 400,
    spaceBetween: 100,
    modules: [Navigation, Pagination, Autoplay],
    autoHeight: true,
    autoplay: {
      delay: 5000,
      disableOnInteraction: false,
    },
    navigation: {
      nextEl: '#offer-1-autos-next',
      prevEl: '#offer-1-autos-prev',
    },
    pagination: {
      el: '#offer-1-autos-pagination',
      clickable: true,
    },
  });

  const prevExtra = document.getElementById('offer-1-autos-prev-extra');
  const nextExtra = document.getElementById('offer-1-autos-next-extra');

  // offer-1-autos-extra
  const extraContainer = document.getElementById('offer-1-autos-extra');
  const extraSlides = Array.from(extraContainer?.children ?? []);

  const getBounds = () => {
    const lastIndex =
      extraSlides.length > 0
        ? extraSlides.length - 1
        : swiper.slides.length - 1;

    if (extraSlides.length > 0) {
      return {
        isFirst: swiper.activeIndex <= 0,
        isLast: swiper.activeIndex >= lastIndex,
        lastIndex,
      };
    }

    return {
      isFirst: swiper.isBeginning,
      isLast: swiper.isEnd,
      lastIndex,
    };
  };

  prevExtra?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();

    const { isFirst } = getBounds();
    if (isFirst) return;

    const prevIndex = Math.max(swiper.activeIndex - 1, 0);
    swiper.slideTo(prevIndex);
  });
  nextExtra?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();

    const { isLast, lastIndex } = getBounds();
    if (isLast) return;

    const nextIndex = Math.min(swiper.activeIndex + 1, lastIndex);
    swiper.slideTo(nextIndex);
  });

  const syncExtra = (index) => {
    extraSlides.forEach((el, i) => {
      el.classList.toggle('is-active', i === index);
    });
  };

  const syncExtraNav = () => {
    const { isFirst, isLast } = getBounds();

    if (prevExtra) {
      prevExtra.classList.toggle('swiper-button-disabled', isFirst);
    }
    if (nextExtra) {
      nextExtra.classList.toggle('swiper-button-disabled', isLast);
    }
  };

  syncExtraNav();
  swiper.on('slideChange', () => {
    if (extraSlides.length > 0) {
      syncExtra(swiper.activeIndex);
    }
    syncExtraNav();
  });

  if (extraSlides.length > 0) {
    syncExtra(swiper.realIndex);
  }
}

function setLandingAutosFirstAutos() {
  if (!document.getElementById('autos-1-autos')) return;
  new Swiper('#autos-1-autos', {
    speed: 400,
    slidesPerView: 'auto',
    spaceBetween: 16,
    modules: [Navigation],
    navigation: {
      nextEl: '#autos-1-autos-next',
      prevEl: '#autos-1-autos-prev',
    },
    breakpoints: {
      1024: {
        slidesPerView: 4,
        spaceBetween: 16,
      },
    },
  });
}

function setLandingReviewsFirstReviews() {
  const el = document.getElementById('reviews-1-reviews');
  if (!el) return;

  // const ID = el.dataset.id;
  const type = el.dataset.type;
  const typeList = el.dataset.typeList;
  const query = el.dataset.query;

  const swiper = new Swiper('#reviews-1-reviews', {
    modules: [Navigation],
    autoHeight: true,
    breakpoints: {
      768: {
        autoHeight: false,
      },
    },
    speed: 400,
    slidesPerView: 'auto',
    grabCursor: true,
    watchSlidesProgress: true,
    navigation: {
      nextEl: '#reviews-1-reviews-next',
      prevEl: '#reviews-1-reviews-prev',
    },
    on: {
      slideChange: function () {
        if (this.isLoadedLazySlides) {
          return;
        }
        updateSwiper(swiper, type, query, typeList);
        this.isLoadedLazySlides = true;
      },
    },
  });
}
